import { useMutation } from '@tanstack/vue-query'
import type { UpdateContractInput } from '~/resources/graphql/graphql'
import { graphql } from '~/resources/graphql'
import { useRpQuery } from '~/composables/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useSetCartData } from '~/composables/use-cart'
import { useViewer } from '#imports'
import { useSelectedLanguage } from '~/composables/use-selected-language'

export function useProgramUpdateContractMutation() {
    const query = useRpQuery()
    const selectedLanguage = useSelectedLanguage()
    const activeFacility = useActiveFacility()
    const setCartData = useSetCartData()
    const viewer = useViewer()

    return useMutation({
        mutationFn: async (mutationData: UpdateContractInput) => {
            const response = await query(
                graphql(/** @lang GraphQL */ `
                    mutation ProgramUpdateContractMutation(
                        $input: UpdateContractInput!
                        $facilityId: ID!
                        $language: Language!
                        $capacityResultFilter: CapacityResultFilter!
                        $bookingStatusFilter: BookingStatusAvailableFilter!
                    ) {
                        updateContract(input: $input) {
                            __typename
                            ... on UpdateContractResult {
                                record {
                                    __typename
                                    ... on PlanContract {
                                        ...PurchaseProgramContractFragment
                                        ...AnalyticsAddToCartResultFragment
                                    }
                                }
                                query {
                                    viewer {
                                        cart(facilityId: $facilityId) {
                                            ...ViewerCartFragment
                                        }
                                    }
                                }
                            }
                            ... on ValidationError {
                                fieldErrors {
                                    message
                                }
                            }
                        }
                    }
                `),
                {
                    input: mutationData,
                    facilityId: activeFacility.value.id,
                    language: selectedLanguage.value,
                    capacityResultFilter: {
                        ignoreContractId: mutationData.id
                    },
                    bookingStatusFilter: {
                        customerId: viewer.value?.customerId ?? null
                    }
                }
            )

            if (response.updateContract.__typename === 'UpdateContractResult') {
                // Update the cart data
                if (response.updateContract.query.viewer !== null) {
                    setCartData(response.updateContract.query.viewer.cart)
                }
                return response.updateContract
            } else {
                throw new Error('Error updating contract')
            }
        }
    })
}
